import styled from 'styled-components';
import {Detail, EMPHASIS, Icon, Label} from '@forecast-it/design-system';
import {LabelledButton} from '../../tickets/components/LabelledButton';
import {groupSubtotals} from '../utils/groupStatsFormatting';
import type {TicketGroupStats} from '../utils/groupTickets';

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;

const GroupLabel = styled(Label)`
	font-weight: 600;
`;

const Stats = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
`;

interface Props {
	/** DOM-safe, result-unique form of the group's key — see `TicketGroup.domKey`. */
	domKey: string;
	label: string;
	stats: TicketGroupStats;
	collapsed: boolean;
	/** id of the group's row container, for aria-controls. */
	contentId: string;
	onToggle: () => void;
}

/**
 * Expandable header for one group in the dashboard's grouped view: a disclosure
 * chevron, the group's label, and its subtotals (count, % resolved, median
 * resolution).
 *
 * The subtotals always render, showing a dash where there is nothing to compute,
 * rather than disappearing — a header whose fields come and go makes two groups
 * side by side impossible to scan against each other, and an absent median is
 * itself information ("nothing in this bucket has been resolved").
 */
export function DashboardGroupHeader({domKey, label, stats, collapsed, contentId, onToggle}: Props) {
	const subtotals = groupSubtotals(stats);
	return (
		<Row data-testid={`dashboard-group-header-${domKey}`}>
			{/* The design-system Button derives its own aria-label from its children and
			    applies it last, so an icon-only button ends up named `[object Object]`.
			    LabelledButton is what gives it a dependable name. */}
			<LabelledButton
				emphasis={EMPHASIS.LOW}
				label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
				aria-expanded={!collapsed}
				aria-controls={contentId}
				data-testid={`dashboard-group-toggle-${domKey}`}
				onPress={onToggle}
			>
				<Icon icon={collapsed ? 'chevronRight' : 'chevronDown'} size="s" />
			</LabelledButton>

			<GroupLabel>{label}</GroupLabel>

			{/* `medium` is Detail's auxiliary-grey key; Detail has no `auxiliary` key of
			    its own, and an unknown key resolves to no colour at all. */}
			<Stats>
				<Detail color="medium" data-testid={`dashboard-group-count-${domKey}`}>
					{subtotals.count}
				</Detail>
				<Detail color="medium" data-testid={`dashboard-group-resolved-${domKey}`}>
					{subtotals.resolved} resolved
				</Detail>
				<Detail color="medium" data-testid={`dashboard-group-median-${domKey}`}>
					{subtotals.median} median resolution
				</Detail>
			</Stats>
		</Row>
	);
}
